import { Box, Typography, useTheme } from '@mui/material'
import { FormikProvider, useFormik } from 'formik'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import useBulkAssignAgents from '~/features/agents/hooks/useBulkAssignAgents'
import useCreateAgent from '~/features/agents/hooks/useCreateAgent'
import { EAgentKeys, TCreateAssistant } from '~/features/agents/models'
import { createAssistantValidationSchema } from '~/features/agents/utils'
import CreateFormCard from '~/features/onboarding/components/firstAssistantOnboarding/createFormCard'
import FeaturesCard from '~/features/onboarding/components/firstAssistantOnboarding/featuresCard'
import FreePlanCard from '~/features/onboarding/components/firstAssistantOnboarding/freePlanCard'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import {
  EEmailStatus,
  EPhoneStatus,
  ETextStatus,
} from '~/features/settings/models'
import { EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import { store } from '~/store'
import { getTimezone } from '~/utils/date'
import rem from '~/utils/ui'

function FirstAssistantOnboarding() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { partner } = usePartnerCentralized()
  const partnerId = partner.id
  const { supportEmail } = partner

  const [orgId, paymentTier, setUIElements] = store((state) => [
    state.user.organizationId,
    state.organization.paymentTier,
    state.setUIElements,
  ])

  const { mutateAsync: createAgent, isPending: isCreating } = useCreateAgent()
  const { mutateAsync: bulkAssignAgents } = useBulkAssignAgents()

  const isFreeUser = paymentTier === ECognitoGroups.customerFreePlan

  const formik = useFormik<TCreateAssistant>({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnBlur: true,
    validationSchema: createAssistantValidationSchema,
    initialValues: {
      [EAgentKeys.agentReferenceName]: '',
      [EAgentKeys.businessName]: '',
      [EAgentKeys.service]: '',
      [EAgentKeys.locationId]: '',
    },
    onSubmit: async (values) => {
      try {
        const response = await createAgent({
          payload: {
            agentReferenceName: values.agentReferenceName,
            businessName: values.businessName || '',
            service: values.service,
            partnerId,
            orgId,
            phoneStatus: EPhoneStatus.disabled,
            textStatus: ETextStatus.disabled,
            emailStatus: EEmailStatus.disabled,
            agentTimezone: getTimezone(),
            customURL: 'knowledge',
          },
        })

        const agentId = response?.data?.id
        const locationId = values[EAgentKeys.locationId]?.trim()

        if (agentId && locationId) {
          await bulkAssignAgents({
            assignments: [
              {
                agentId,
                locationId,
              },
            ],
          })
        }

        toast.success('Assistant created successfully')
        setUIElements({ isGuidanceOpen: true })
        navigate(EClientPath.settingsKnowledge)
      } catch {
        toast.error(
          `An error occurred, if this continues, reach out to us at ${supportEmail}`,
        )
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <Box
        sx={{
          width: '100%',
          minHeight: '100%',
          padding: { xxxs: rem(12), lg: rem(24) },
          overflow: 'auto',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            marginBottom: rem(48),
            maxWidth: rem(800),
            mx: 'auto',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: rem(36),
              color: theme.palette.text.primary,
              marginBottom: rem(16),
              lineHeight: 1.2,
            }}
          >
            Welcome to {partner.partnerName}!
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: rem(18),
              color: theme.palette.text.secondary,
              marginBottom: rem(12),
              lineHeight: 1.4,
            }}
          >
            You&apos;re just one step away from transforming your business.
          </Typography>
          {!isFreeUser && (
            <Typography
              variant="body1"
              sx={{
                fontSize: rem(16),
                color: theme.palette.text.secondary,
                lineHeight: 1.5,
              }}
            >
              Create your first AI assistant to start automating customer
              interactions.
            </Typography>
          )}
        </Box>

        {/* Content Section - Two Cards Side by Side */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: rem(24),
            minHeight: rem(350),
            maxWidth: rem(1000),
            mx: 'auto',
            width: '100%',
          }}
        >
          {/* Left Card - Features */}
          <FeaturesCard />

          {/* Right Card - Create Form */}

          {isFreeUser ? (
            <FreePlanCard />
          ) : (
            <CreateFormCard isCreating={isCreating} />
          )}
        </Box>
      </Box>
    </FormikProvider>
  )
}

export default FirstAssistantOnboarding
